import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { LegalHeader, LegalFooter } from "../legal/Terms";
import useSEO from "../../lib/useSEO";
import { trackEvent, useTrackOnMount } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pmv5_csuite_token";

/**
 * Zero-friction executive demo intake. Four fields, no card, no verification.
 * On submit we call `POST /api/csuite/demo/start`, stash the returned JWT, and
 * jump into the C-Suite dashboard where the demo banner renders automatically.
 */
export default function CSuiteDemoStart() {
  useSEO({
    title: "Explore the Executive Portal · Capittance Harmonizer Demo",
    description: "Enter a preconfigured executive workspace with six fictional projects, NAVBOT AI and a sample Board Pack. No credit card, no sales call.",
    path: "/csuite/demo",
  });
  const nav = useNavigate();
  const [f, setF] = useState({ first_name: "", email: "", company: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Preserve UTM params so leads carry attribution end-to-end.
  const utm = React.useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return {
        utm_source: p.get("utm_source") || undefined,
        utm_medium: p.get("utm_medium") || undefined,
        utm_campaign: p.get("utm_campaign") || undefined,
      };
    } catch { return {}; }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.first_name.trim() || !/\S+@\S+\.\S+/.test(f.email) || !f.company.trim() || !f.role.trim()) {
      setErr("All four fields are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/csuite/demo/start`, { ...f, ...utm }, { timeout: 15000 });
      const token = r.data?.token;
      if (!token) throw new Error("No token returned");
      try { localStorage.setItem(TOKEN_KEY, token); } catch { /* private mode */ }
      // Fire *before* redirect so the beacon is in-flight during page unload.
      trackEvent("executive_demo_started", { company: f.company, role: f.role });
      // Full reload so the CSuiteAuthProvider re-bootstraps with the fresh token.
      window.location.href = "/csuite/dashboard?welcome=demo";
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Could not open the demo. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <LegalHeader title="Explore the Executive Portal" subtitle="A preconfigured demo — no credit card, no sales call" />
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="page-csuite-demo">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-[#FDF2F4] text-[#7A1F2B] font-mono text-[10px] uppercase tracking-[0.2em] mb-3">
            <Sparkles size={11}/> Demonstration environment
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900">
            Enter the Executive Portal in <span className="text-[#7A1F2B]">30 seconds</span>.
          </h1>
          <p className="text-sm text-zinc-600 mt-3 max-w-xl mx-auto leading-relaxed">
            Six fictional programmes are already loaded. Explore portfolio health, drill into a red project,
            ask NAVBOT executive questions, and generate a watermarked Board Pack — grounded in demonstration data only.
          </p>
        </div>

        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto" data-testid="demo-intake-form">
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">First name</div>
            <input type="text" className="fld py-2 px-3 w-full" value={f.first_name}
                   onChange={e => setF({ ...f, first_name: e.target.value })}
                   required maxLength={80} data-testid="demo-first-name"/>
          </label>
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Work email</div>
            <input type="email" className="fld py-2 px-3 w-full" value={f.email}
                   onChange={e => setF({ ...f, email: e.target.value })}
                   required maxLength={120} data-testid="demo-email"/>
          </label>
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Company</div>
            <input type="text" className="fld py-2 px-3 w-full" value={f.company}
                   onChange={e => setF({ ...f, company: e.target.value })}
                   required maxLength={120} data-testid="demo-company"/>
          </label>
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Role</div>
            <input type="text" className="fld py-2 px-3 w-full" value={f.role}
                   placeholder="e.g. CIO, CFO, Head of PMO"
                   onChange={e => setF({ ...f, role: e.target.value })}
                   required maxLength={80} data-testid="demo-role"/>
          </label>

          {err && <div className="md:col-span-2 p-3 bg-red-50 border-l-4 border-red-500 text-sm text-red-800 rounded-sm" data-testid="demo-error">{err}</div>}

          <div className="md:col-span-2 flex flex-col items-center gap-2 mt-2">
            <button type="submit" disabled={busy}
                    className="btn btn-primary inline-flex items-center gap-2 min-w-[240px] justify-center"
                    data-testid="demo-submit">
              {busy ? <><Loader2 size={14} className="animate-spin"/> Opening your workspace…</> : <>Open Executive Portal <ArrowRight size={14}/></>}
            </button>
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              No credit card · No sales call · Sample data only
            </div>
          </div>
        </form>

        <div className="mt-10 max-w-2xl mx-auto p-4 rounded-sm border border-[var(--border)] bg-zinc-50 text-xs text-zinc-600 leading-relaxed">
          <div className="flex items-start gap-2">
            <ShieldCheck size={14} className="mt-0.5 text-zinc-400 shrink-0"/>
            <div>
              <b className="text-zinc-800">Your data is safe.</b> The demo uses a shared read-only workspace preloaded with
              fictional projects. You cannot see any real customer information; real customers cannot see anything you enter.
              Ready to use Capittance with your own data? Start an Executive Trial from the dashboard.
            </div>
          </div>
        </div>

        <LegalFooter />
      </main>
    </div>
  );
}
